'use client';

import {useEffect} from 'react';
import {ErrorSection} from '@/app/_components/shared/error-section';
import {LabelsProvider, useLabel} from '@/app/_contexts/labels-context';
// CSS is imported by the app's global-error.tsx, not here

interface GlobalErrorProps {
    error: Error & { digest?: string };
    reset: () => void;
}

function GlobalErrorContent({error, reset}: GlobalErrorProps) {
    const tagline = useLabel('criticalErrorTagline', 'Critical Error');
    const title = useLabel('criticalErrorTitle', 'System Failure');
    const description = useLabel('criticalErrorDescription', 'A critical error occurred that prevented the application from loading properly. This is likely due to a system-level failure.');
    const resetButtonLabel = useLabel('resetButtonLabel', 'Try again');
    const defaultButtonLabel = useLabel('homeButtonLabel', 'Go to homepage');

    useEffect(() => {
        // Log critical error to monitoring service
        console.error('Critical application error:', error);

        // Forward to Sentry when configured. Dynamic import keeps @sentry/nextjs
        // out of the client bundle for apps that don't set a DSN.
        if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
            void import('@sentry/nextjs').then((Sentry) => {
                Sentry.captureException(error, {
                    level: 'fatal',
                    tags: { errorBoundary: 'global', critical: true },
                    extra: { digest: error.digest },
                });
            });
        }
    }, [error]);

    return (
        <ErrorSection
            tagline={tagline}
            title={title}
            description={description}
            resetButtonLabel={resetButtonLabel}
            defaultButtonLabel={defaultButtonLabel}
            showResetButton={true}
            onReset={reset}
            error={error}
        />
    );
}

export default function GlobalError(props: GlobalErrorProps) {
    return (
        <html lang="de">
      <body>
        <LabelsProvider labels={{}}>
          <GlobalErrorContent {...props} />
        </LabelsProvider>
      </body>
    </html>
    );
}
